<script lang="ts" setup>
import Logo from "@src/components/views/HomeView/MainMenu/Logo.vue";
import {computed, ref} from "vue";
import MenuItem from "@src/components/views/HomeView/MainMenu/components/MenuItem.vue";
import useStore from "@src/stores/store";
import {
    ChatBubbleLeftRightIcon,
    ChatBubbleOvalLeftIcon,
    ClockIcon,
    MoonIcon,
    PauseCircleIcon,
    SunIcon,
    UserGroupIcon,
    ViewColumnsIcon,
} from "@heroicons/vue/24/solid";
import {ISection} from "@src/types";
import RobotIcon from "@src/components/ui/icons/RobotIcon.vue";
import AccountDropdown from "@src/components/views/HomeView/MainMenu/AccountDropdown.vue";
import MoreSheet from "@src/components/views/HomeView/MainMenu/components/MoreSheet.vue";
import {useChatSessionStore} from "@src/stores/chatSession";
import {useChatSessions} from "@src/composables/useChatSessions";
import {useInternalChatStore} from "@src/stores/internalChat";
import {useGroups} from "@src/composables/useGroups";
import {useIsMobile} from "@src/composables/useIsMobile";

const {getSessionsWithNewMessagesCount, getBotSessionsWithNewMessagesCount} = useChatSessions()
const activeCount = getSessionsWithNewMessagesCount('active')
const holdCount = getSessionsWithNewMessagesCount('hold')
const internalChatStore = useInternalChatStore()

const { isMobile } = useIsMobile()


const store = useStore()
const sectionList = computed<ISection[]>(() => {
    return [
        {
            icon: ChatBubbleOvalLeftIcon,
            title: 'Ativos',
            active: store.activeSidebarComponent === 'active',
            notifications: activeCount,
            showTitle: true,
            value: 'active',
          information: 'Acompanhe seus atendimentos em andamento.'
        },
        {
            icon: PauseCircleIcon,
            title: 'Espera',
            active: store.activeSidebarComponent === 'holding',
            notifications: holdCount,
            showTitle: true,
            value: 'holding',
          information: 'Acesse os atendimentos que aguardam um operador.'
        },
        {
            icon: RobotIcon,
            title: 'Fluxo',
            notifications: 0,
            active: store.activeSidebarComponent === 'bot',
            showTitle: true,
            value: 'bot',
            showOnMobile: false,
          information: 'Gerencie os clientes que estão interagindo com a IA.'
        },
        {
            icon: UserGroupIcon,
            title: 'Grupos',
            active: store.activeSidebarComponent === 'group',
            showTitle: true,
            value: 'group',
          information: 'Visualize e participe dos seus grupos no Whatsapp.'
        },
        {
            icon: ViewColumnsIcon,
            title: 'Kanban',
            notifications: 0,
            active: store.activeSidebarComponent === 'kanban',
            showTitle: true,
            value: 'kanban',
            showOnMobile: false,
          information: 'Organize e acompanhe os atendimentos por etapa.'
        },
        {
            icon: ClockIcon,
            title: 'Mensagens Agendadas',
            notifications: 0,
            active: store.activeSidebarComponent === 'schedule-messages',
            showTitle: true,
            value: 'schedule-messages',
            showOnMobile: false,
          information: 'Consulte e gerencie mensagens programadas para envio futuro'
        },
        {
            icon: ChatBubbleLeftRightIcon,
            title: 'Chat Interno',
            notifications: internalChatStore.totalUnread,
            active: store.activeSidebarComponent === 'internal-chat',
            showTitle: true,
            value: 'internal-chat',
          information: 'Converse com sua equipe internamente.'
        },
    ];
})
const showDropdown = ref<boolean>(false);
const sessionStore = useChatSessionStore()


function ChangeActiveSidebar(value: string){
    sessionStore.actionSessions = []
    sessionStore.openActionsBulk = false
    store.activeSidebarComponent = value
}
</script>

<template>
    <div class="xs:w-full md:w-12 md:h-full md:py-7 xs:py-5 px-5
                flex xs:flex-row md:flex-col items-center
                transition-all duration-500 xs:order-1 md:order-0
                col-span-1 relative z-50 min-h-0">
<!--        logo-->
        <Logo/>

<!--        navigation-->
        <div class="grow min-h-0 overflow-y-auto md:flex-1 xs:flex-none w-full">
            <ul class="xs:flex md:block xs:justify-between xs:items-center overflow-x-hidden overflow-y-auto">
                <li
                    v-for="section in sectionList"
                    :key="section.value"
                    v-show="section.showOnMobile !== false || !isMobile"
                >
                    <MenuItem
                        :active="store.activeSidebarComponent === section.value"
                        :icon="section.icon"
                        :notifications="section?.notifications"
                        :show-title=section.showTitle
                        :title="section.title"
                        :information="section.information"
                        @click="ChangeActiveSidebar(section.value)"
                    />
                </li>
                <li v-if="isMobile">
                    <MoreSheet />
                </li>
            </ul>
        </div>
<!--        secondary navigation-->
        <div class="xs:hidden md:block mt-5 flex-shrink-0">
            <ul>
                <!--toggle dark mode button-->
                <li>
                    <MenuItem
                        :active="false"
                        :icon="store.settings.darkMode ? SunIcon : MoonIcon"
                        title="Night mode"
                        @click="store.changeTheme()"
                    />
                </li>
            </ul>
            <!--separator-->
            <hr
                class="xs:hidden md:block mb-6 border-gray-100 dark:border-gray-600"
            />

            <!--user avatar-->
            <AccountDropdown
                id="profile-menu"
                :handle-close-dropdown="() => (showDropdown = false)"
                :handle-show-dropdown="() => (showDropdown = true)"
                :show-dropdown="showDropdown"
                aria-labelledby="profile-menu-button"
                class="xs:hidden md:block cursor-pointer"
            />
        </div>
    </div>
</template>


<style scoped>

</style>